import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterCombo, type FilterComboOption } from '@/components/ui/filter-combo';
import { filterTriggerLabel } from '@/lib/filter-trigger-label';
import {
  runSequentialAdd,
  sequentialAddProgressText,
  sequentialAddSummary,
  sequentialAddWarning,
  type SequentialAddNoun,
  type SequentialAddSummary,
} from '@/lib/sequential-add';
import { cn } from '@/lib/utils';
import { DARK_BUTTON, TEXT_BUTTON } from '@/routes/admin/campaigns/detail-ui';

// =============================================================================
// entity-scope-picker.tsx — Picker de périmètre RÉUTILISABLE (Clients / Fournisseurs) du détail campagne.
// =============================================================================
// Liste des membres du périmètre (dénomination + identifiant) avec « Retirer » par ligne, puis zone
// d'AJOUT. PRÉSENTATIONNEL : la source des candidats et les mutations sont fournies par le panneau
// parent (Clients = référentiel client-side, Fournisseurs = recherche server-side). Sans PII.
//
// Deux habillages : `panel` (lignes plates à gouttière px-6 d'un Panel) et `card` (carte étroite de la
// colonne droite, « Retirer » en lien texte).
//
// -----------------------------------------------------------------------------------------------
// LOT F1-bis (2026-09-21) — la zone d'ajout passe à la LISTE DÉROULANTE AUTOFILTRANTE MULTI-SÉLECTION
// -----------------------------------------------------------------------------------------------
// Décision GATE_HUMAN du 2026-09-21 (verbatim) : « Sélection multiple (cases à cocher) », « Liste
// défilante complète », zone campagne d'abord. John a testé le nouveau filtre depuis l'onglet DÉTAIL
// (« j'ai créeé une nouvelle campagne et utilisé les filtres fournisseurs et client ») : ce sont ces
// deux panneaux-là, restés sur l'ancien champ de recherche au lot F1.
//
// Ce que ce composant CONSOMME sans le réécrire : `FilterCombo` (components/ui) porte le champ de
// filtre, le tri alphabétique, le pliage casse/accents, la liste défilante complète, le parcours
// clavier et le panneau PORTALÉ (donc jamais rogné par la carte étroite, et basculé au-dessus du
// déclencheur quand la place manque en bas de page).
//
// CHANGEMENT DE COMPORTEMENT ASSUMÉ. Cliquer un candidat ne l'ajoutait qu'immédiatement, un appel par
// ajout. Désormais l'utilisateur COCHE n entrées puis valide une fois, « Ajouter (n) ».
//
// POURQUOI « AJOUTER (n) » VIT HORS DU PANNEAU DÉROULANT. Le panneau se ferme au clic extérieur et à
// Échap ; y loger la validation, l'avancement et le compte rendu d'échec reviendrait à afficher le
// rapport d'erreur dans une surface qui disparaît. Le bouton, l'avertissement de volume, l'avancement
// et le compte rendu restent donc dans le panneau de la page, sous le déclencheur, et survivent à la
// fermeture de la liste. La sélection cochée, elle, vit ici et non dans `FilterCombo` (composant
// contrôlé) : elle est donc préservée quand la liste se ferme et se rouvre.

/** Une occurrence sélectionnable (candidat à l'ajout) : identifiant + dénomination. */
export interface ScopeCandidate {
  readonly id: string;
  readonly denomination: string;
}

/** Une entrée déjà ajoutée au périmètre : l'entryId sert au retrait, id/dénomination à l'affichage. */
export interface ScopeMember {
  readonly entryId: string;
  readonly id: string;
  readonly denomination: string;
  /** Badge optionnel (ex. état de participation fournisseur) rendu à côté de la dénomination. */
  readonly badge?: {
    readonly label: string;
    readonly variant: 'default' | 'secondary' | 'outline' | 'accent';
  };
}

export interface EntityScopePickerProps {
  readonly entityNoun: string;
  /** Nom du déclencheur et du panneau, ex. « Clients à ajouter ». */
  readonly pickerLabel: string;
  /** Accord du compte rendu d'ajout (« 2 clients ajoutés au périmètre. »). */
  readonly noun: SequentialAddNoun;
  readonly idLabel: string;
  /** Mise en forme de l'identifiant AFFICHÉ dans la liste des membres (ex. SIRET groupé). */
  readonly formatId?: ((id: string) => string) | undefined;
  readonly searchPlaceholder: string;
  /** Candidats proposés, MEMBRES DÉJÀ EXCLUS par le parent. Le tri est fait par `FilterCombo`. */
  readonly candidates: readonly ScopeCandidate[];
  /** Notifié à chaque frappe quand la recherche est SERVEUR (fournisseurs). Optionnel. */
  readonly onSearchChange?: ((query: string) => void) | undefined;
  /** Mention rendue sous le déclencheur (ex. troncature de la page serveur). */
  readonly searchNote?: React.ReactNode | undefined;
  /** Ajout UNITAIRE d'un identifiant. Rejette en cas d'échec : le rejet est capturé et NOMMÉ. */
  readonly onAdd: (id: string) => Promise<unknown>;
  readonly members: readonly ScopeMember[];
  readonly onRemove: (entryId: string) => void;
  readonly removePending: boolean;
  readonly emptyMembersText: string;
  readonly variant: 'panel' | 'card';
  /** Contenu rendu entre la liste des membres et la zone d'ajout. */
  readonly footerNote?: React.ReactNode | undefined;
}

export function EntityScopePicker({
  entityNoun,
  pickerLabel,
  noun,
  idLabel,
  formatId = (id) => id,
  searchPlaceholder,
  candidates,
  onSearchChange,
  searchNote,
  onAdd,
  members,
  onRemove,
  removePending,
  emptyMembersText,
  variant,
  footerNote,
}: EntityScopePickerProps): React.JSX.Element {
  const isPanel = variant === 'panel';

  // Sélection EN ATTENTE d'ajout. État in-memory React (RULE_REF_006 : aucun stockage navigateur).
  const [pending, setPending] = React.useState<readonly string[]>([]);
  // Avancement de l'enchaînement, `null` hors ajout. Sert aussi de verrou anti-double-soumission.
  const [progress, setProgress] = React.useState<{
    readonly done: number;
    readonly total: number;
  } | null>(null);
  const [summary, setSummary] = React.useState<SequentialAddSummary | null>(null);

  const denominationById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const candidate of candidates) {
      map.set(candidate.id, candidate.denomination);
    }
    for (const member of members) {
      map.set(member.id, member.denomination);
    }
    return map;
  }, [candidates, members]);

  const options = React.useMemo<readonly FilterComboOption[]>(
    () =>
      candidates.map((candidate) => ({
        value: candidate.id,
        label: candidate.denomination,
        // L'identifiant est donné BRUT, pas groupé : il devient ainsi cherchable en chiffres continus
        // (« 55210055400013 »), ce qu'un SIRET formaté en tranches interdirait. La liste des membres,
        // elle, continue d'afficher l'identifiant formaté, où c'est la lisibilité qui prime.
        hint: candidate.id,
      })),
    [candidates],
  );

  // Les dénominations des entrées cochées, pour un déclencheur qui DIT son état en toutes lettres
  // (WCAG 1.4.1 : jamais la seule couleur). Grammaire commune à tous les écrans du lot F1.
  const pendingLabels = React.useMemo(
    () => pending.map((id) => denominationById.get(id) ?? id),
    [pending, denominationById],
  );
  const triggerLabel = filterTriggerLabel(pickerLabel, pendingLabels, 'aucun sélectionné');
  const warning = sequentialAddWarning(pending.length, noun);
  const busy = progress !== null;

  const submit = async (): Promise<void> => {
    if (pending.length === 0 || busy) {
      return;
    }
    setSummary(null);
    setProgress({ done: 0, total: pending.length });
    const outcome = await runSequentialAdd(pending, onAdd, (done, total) => {
      setProgress({ done, total });
    });
    setProgress(null);
    // Les entrées passées disparaissent des candidats (elles sont devenues membres) ; celles qui ont
    // ÉCHOUÉ restent cochées, prêtes pour une nouvelle tentative délibérée. Rien n'est perdu en
    // silence, et l'utilisateur n'a pas à retrouver ses cases une à une.
    setPending(outcome.failed.map((failure) => failure.id));
    setSummary(
      sequentialAddSummary(outcome, noun, (id) => denominationById.get(id) ?? id),
    );
  };

  return (
    <div>
      {members.length === 0 ? (
        <p
          role="status"
          className={cn('text-sm text-muted-foreground', isPanel ? 'px-6 py-4' : 'pb-1')}
        >
          {emptyMembersText}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {members.map((m) => (
            <li
              key={m.entryId}
              className={cn(
                'flex items-center justify-between gap-4',
                isPanel ? 'px-6 py-3.5' : 'py-2.5 first:pt-0',
              )}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{m.denomination}</span>
                <span className="block font-mono text-xs text-muted-foreground">
                  {idLabel} {formatId(m.id)}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                {m.badge !== undefined ? (
                  <Badge variant="outline">{m.badge.label}</Badge>
                ) : null}
                {isPanel ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={removePending}
                    onClick={() => {
                      onRemove(m.entryId);
                    }}
                    aria-label={`Retirer ${m.denomination} du périmètre`}
                  >
                    Retirer
                  </Button>
                ) : (
                  <button
                    type="button"
                    className={TEXT_BUTTON}
                    disabled={removePending}
                    onClick={() => {
                      onRemove(m.entryId);
                    }}
                    aria-label={`Retirer ${m.denomination} du périmètre`}
                  >
                    Retirer
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {footerNote}

      <div
        role="group"
        aria-label={`Ajout ${entityNoun} au périmètre`}
        className={cn('space-y-2', isPanel ? 'border-t border-border px-6 py-3' : 'pt-3')}
      >
        <div className="flex flex-wrap items-center gap-2">
          <FilterCombo
            triggerLabel={triggerLabel}
            groupLabel={pickerLabel}
            options={options}
            selected={pending}
            onChange={setPending}
            onQueryChange={onSearchChange}
            searchPlaceholder={searchPlaceholder}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={DARK_BUTTON}
            disabled={pending.length === 0 || busy}
            onClick={() => {
              void submit();
            }}
          >
            {`Ajouter (${String(pending.length)})`}
          </Button>
        </div>

        {searchNote}

        {warning === null ? null : (
          // Avertissement SOBRE (décision GATE_HUMAN) : il informe du volume d'appels, il ne bloque rien.
          <p role="status" className="text-xs text-muted-foreground">
            {warning}
          </p>
        )}

        {progress === null ? null : (
          <p role="status" className="text-xs text-muted-foreground">
            {sequentialAddProgressText(progress.done, progress.total)}
          </p>
        )}

        {summary === null ? null : (
          // Un échec partiel est une ALERTE : ce qui est passé reste au périmètre, ce qui a échoué est
          // nommé avec le message de l'API. Le ton est doublé par le texte, jamais porté par la couleur.
          <p
            role={summary.tone === 'ok' ? 'status' : 'alert'}
            className={cn(
              'text-sm',
              summary.tone === 'ok' ? 'text-muted-foreground' : 'text-destructive',
            )}
          >
            {summary.text}
          </p>
        )}
      </div>
    </div>
  );
}
